import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Grid,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { toast } from "react-toastify";
import { postDetailsByPostId, uploadMedia } from "../../../services/postService";
import insta from "../../../assets/socialmedia/instagram.svg";
import facebook from "../../../assets/socialmedia/faceBuk.svg";
import linkedin from "../../../assets/socialmedia/linkdn.svg";
import x from "../../../assets/socialmedia/x.svg";
import youtube from "../../../assets/socialmedia/youtube.svg";
import { getResolvedPosterUrl, BLACK_THUMBNAIL } from "../../../shared/mediaResolver";

const platformOptions = [
  { name: "LinkedIn", key: "linkedin", icon: linkedin },
  { name: "Twitter / X", key: "x", icon: x },
  { name: "Instagram", key: "instagram", icon: insta },
  { name: "YouTube", key: "youtube", icon: youtube },
  { name: "Facebook", key: "facebook", icon: facebook },
];

const EditScheduleModal = ({ open, onClose, scheduleData, onSave, onDelete, readOnly = false }) => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [content, setContent] = useState("");
  const [variations, setVariations] = useState([]);
  const [isVariation, setIsVariation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);

  // Per-variation media: array of arrays, index = variation index
  // Each entry: [{ id, fileUrl, mediaType, thumbnailUrl, fileName, globalMediaId }]
  const [variationMediaList, setVariationMediaList] = useState([]);
  // Single-content media (non-variation posts)
  const [singleMediaList, setSingleMediaList] = useState([]);

  // Track which variation + slot is being replaced/added
  const [activeVariationIndex, setActiveVariationIndex] = useState(null);
  const [replacingSlotIndex, setReplacingSlotIndex] = useState(null); // null = adding new
  const [uploadingKey, setUploadingKey] = useState(null); // "v{varIdx}-{slotIdx}" or "single-{slotIdx}"
  const fileInputRef = useRef(null);

  const isReadOnly = scheduleData?.isSuccess === true || scheduleData?.isProcessed === true || readOnly;

  // ── Fetch & parse post data ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !scheduleData?.postId) {
      if (!open) {
        setVariationMediaList([]);
        setSingleMediaList([]);
        setActiveVariationIndex(null);
        setReplacingSlotIndex(null);
      }
      return;
    }

    const fetchAndBuild = async () => {
      setMediaLoading(true);
      try {
        // Get all post media (flat list with fileUrls)
        let allMedia = [];
        if (scheduleData.media?.length > 0) {
          allMedia = scheduleData.media;
        } else {
          const response = await postDetailsByPostId(scheduleData.postId);
          allMedia = response?.post?.media || response?.posts?.[0]?.media || [];
        }

        // Parse variations from content
        const rawContent = scheduleData.content || "";
        let parsed = null;
        try {
          const str = String(rawContent).trim();
          if (str.startsWith("[") && str.endsWith("]")) {
            const escapedStr = str.replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\t/g, "\\t");
            const arr = JSON.parse(escapedStr);
            if (Array.isArray(arr) && arr.length > 0 && arr[0].content !== undefined) {
              parsed = arr;
            }
          }
        } catch (e) {}

        if (parsed) {
          if (scheduleData?.selectedVariationId) {
            const selVarMedia = allMedia.filter(m => String(m?.variationId).toLowerCase() === String(scheduleData.selectedVariationId).toLowerCase());
            setSingleMediaList(selVarMedia.length > 0 ? selVarMedia.map(m => ({ ...m })) : allMedia.map(m => ({ ...m })));
            setVariationMediaList([]);
          } else {
            // Prefer exact per-variation attribution via variationId (set by backend).
            // Fall back to position-based slicing for legacy posts without variationId.
            const hasVariationId = allMedia.some(m => m?.variationId);
            let varMedia;
            if (hasVariationId) {
              varMedia = parsed.map(v => allMedia.filter(m => m?.variationId === v.id).map(m => ({ ...m })));
            } else {
              let offset = 0;
              varMedia = parsed.map(v => {
                const count = (v.libraryMediaIds?.length || 0) + (v.mediaIds?.length || 0);
                const items = count > 0 ? allMedia.slice(offset, offset + count) : [];
                offset += count;
                return items.map(m => ({ ...m }));
              });
            }
            setVariationMediaList(varMedia);
            setSingleMediaList([]);
          }
        } else {
          setVariationMediaList([]);
          setSingleMediaList(allMedia.map(m => ({ ...m })));
        }
      } catch (error) {
        console.error("Failed to fetch post details:", error);
        setVariationMediaList([]);
        setSingleMediaList([]);
      } finally {
        setMediaLoading(false);
      }
    };

    fetchAndBuild();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduleData?.postId, scheduleData?.updatedAt, scheduleData?.selectedVariationId]);

  // ── Parse content/date/platforms ────────────────────────────────────────────
  useEffect(() => {
    if (!scheduleData) return;

    const initialContent = scheduleData.content || "";
    try {
      const str = String(initialContent).trim();
      if (str.startsWith("[") && str.endsWith("]")) {
        const escapedStr = str.replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\t/g, "\\t");
        const parsed = JSON.parse(escapedStr);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].content !== undefined) {
          if (scheduleData.selectedVariationId) {
            const found = parsed.find(v => String(v.id).toLowerCase() === String(scheduleData.selectedVariationId).toLowerCase());
            setContent(found?.content || initialContent);
            setIsVariation(false);
            setVariations([]);
          } else {
            setVariations(parsed);
            setIsVariation(true);
            setContent("");
          }
        } else {
          setContent(initialContent);
          setIsVariation(false);
          setVariations([]);
        }
      } else {
        setContent(initialContent);
        setIsVariation(false);
        setVariations([]);
      }
    } catch (e) {
      setContent(initialContent);
      setIsVariation(false);
      setVariations([]);
    }

    if (scheduleData.scheduledTimeUtc) {
      const utcDate = new Date(scheduleData.scheduledTimeUtc);
      setDate(utcDate.toLocaleDateString("en-CA"));
      setTime(utcDate.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false }));
    }

    if (scheduleData.platforms && Array.isArray(scheduleData.platforms)) {
      setSelectedPlatforms(
        scheduleData.platforms.map(p => {
          const lower = p.toLowerCase();
          return lower === "twitter" || lower === "twitter/x" ? "x" : lower;
        })
      );
    } else {
      setSelectedPlatforms([]);
    }
  }, [scheduleData]);

  // ── File upload handler ──────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || isReadOnly) return;

    const varIdx = activeVariationIndex;
    const slotIdx = replacingSlotIndex;
    const isVariationUpload = varIdx !== null;
    const uploadKey = isVariationUpload ? `v${varIdx}-${slotIdx ?? "new"}` : `single-${slotIdx ?? "new"}`;
    setUploadingKey(uploadKey);

    const formData = new FormData();
    formData.append("files", file);
    let mediaType = "image";
    if (file.type.startsWith("video/")) mediaType = "video";
    else if (file.type === "application/pdf" || file.type.startsWith("application/")) mediaType = "document";
    formData.append("mediaType", mediaType);

    try {
      const response = await uploadMedia(formData);
      if (response?.status === true) {
        const uploaded = response?.mediaUploadData?.mediaDTOS?.[0];
        if (!uploaded) { toast.error("Upload failed"); return; }

        const newMedia = {
          id: uploaded.id,
          fileUrl: uploaded.fileUrl,
          mediaType: uploaded.mediaType || mediaType.toUpperCase(),
          thumbnailUrl: uploaded.thumbnailUrl,
          fileName: file.name,
        };

        if (isVariationUpload) {
          setVariationMediaList(prev => {
            const updated = prev.map(arr => [...arr]);
            if (!updated[varIdx]) updated[varIdx] = [];
            if (slotIdx !== null) {
              updated[varIdx][slotIdx] = newMedia; // replace
            } else {
              updated[varIdx].push(newMedia); // add new
            }
            return updated;
          });
          // Update variation's mediaIds so save payload is correct
          setVariations(prev => {
            const updated = [...prev];
            if (!updated[varIdx].mediaIds) updated[varIdx].mediaIds = [];
            if (slotIdx !== null) {
              // Replace old id
              const old = variationMediaList[varIdx]?.[slotIdx];
              if (old) {
                updated[varIdx].mediaIds = updated[varIdx].mediaIds.filter(id => String(id) !== String(old.id));
                updated[varIdx].libraryMediaIds = (updated[varIdx].libraryMediaIds || []).filter(id => String(id) !== String(old.id));
              }
            }
            updated[varIdx].mediaIds.push(uploaded.id);
            return updated;
          });
        } else {
          setSingleMediaList(prev => {
            const updated = [...prev];
            if (slotIdx !== null) updated[slotIdx] = newMedia;
            else updated.push(newMedia);
            return updated;
          });
        }
        toast.success(slotIdx !== null ? "Media replaced." : "Media added.");
      } else {
        toast.error("Upload failed");
      }
    } catch (error) {
      toast.error(error.message || "Upload error");
    } finally {
      setUploadingKey(null);
      setActiveVariationIndex(null);
      setReplacingSlotIndex(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const triggerUpload = (varIdx, slotIdx) => {
    setActiveVariationIndex(varIdx);
    setReplacingSlotIndex(slotIdx);
    fileInputRef.current?.click();
  };

  const handleDeleteMedia = (varIdx, slotIdx) => {
    if (isReadOnly) return;
    if (varIdx !== null) {
      setVariationMediaList(prev => {
        const updated = prev.map(arr => [...arr]);
        const removed = updated[varIdx]?.[slotIdx];
        updated[varIdx] = updated[varIdx].filter((_, i) => i !== slotIdx);
        // Remove from variation mediaIds/libraryMediaIds
        if (removed) {
          setVariations(prevVars => prevVars.map((v, i) => {
            if (i !== varIdx) return v;
            return {
              ...v,
              mediaIds: (v.mediaIds || []).filter(id => String(id) !== String(removed.id)),
              libraryMediaIds: (v.libraryMediaIds || []).filter(id => String(id) !== String(removed.id)),
            };
          }));
        }
        return updated;
      });
    } else {
      setSingleMediaList(prev => prev.filter((_, i) => i !== slotIdx));
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const getMinDate = () => new Date().toLocaleDateString("en-CA");
  const getMinTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const handleSave = async () => {
    if (!date || !time || selectedPlatforms.length === 0) return;
    const today = getMinDate();
    const nowTime = getMinTime();
    let finalDate = date;
    let finalTime = time;
    if (date < today || (date === today && time < nowTime)) {
      finalDate = today;
      finalTime = nowTime;
      setDate(today);
      setTime(nowTime);
    }

    const combinedDateTime = new Date(`${finalDate}T${finalTime}:00`);
    const platformsForApi = selectedPlatforms.map(p => p.toUpperCase());
    const allMedia = isVariation
      ? variationMediaList.flat()
      : singleMediaList;

    setLoading(true);
    try {
      await onSave({
        ...scheduleData,
        content: isVariation ? JSON.stringify(variations) : content,
        scheduledTimeUtc: combinedDateTime.toISOString().split(".")[0] + "Z",
        platforms: platformsForApi,
        media: allMedia,
        selectedVariationId: scheduleData.selectedVariationId || null,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePlatformToggle = (platformKey) => {
    if (isReadOnly) return;
    setSelectedPlatforms(prev =>
      prev.includes(platformKey) ? prev.filter(p => p !== platformKey) : [...prev, platformKey]
    );
  };

  const isFormValid = date && time && selectedPlatforms.length > 0;

  // ── Render media row for a variation (or single content) ─────────────────────
  const renderMediaRow = (mediaList, varIdx) => {
    if (mediaLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={20} sx={{ color: "#0047AB" }} />
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1, alignItems: "flex-start" }}>
        {mediaList.map((media, slotIdx) => {
          const uploadKey = varIdx !== null ? `v${varIdx}-${slotIdx}` : `single-${slotIdx}`;
          const isUploading = uploadingKey === uploadKey;
          const typeStr = (media?.mediaType || "").toUpperCase();
          const fileUrl = media?.fileUrl || "";
          const isVideo = typeStr === "VIDEO" || !!fileUrl.toLowerCase().match(/\.(mp4|mov|wmv|avi|mkv|webm)$/);
          const isImage = !isVideo && (typeStr === "IMAGE" || !!fileUrl.toLowerCase().match(/\.(jpeg|jpg|gif|png|webp|svg)$/));
          const isDoc = !isVideo && !isImage && (
            typeStr === "DOCUMENT" ||
            typeStr === "DOC" ||
            typeStr === "PDF" ||
            typeStr === "FILE" ||
            /\.(pdf|doc|docx|txt|ppt|pptx|xls|xlsx|csv)(\?|$)/i.test(fileUrl)
          );

          return (
            <Box
              key={slotIdx}
              sx={{
                position: "relative",
                width: 80,
                height: 80,
                minWidth: 80,
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid #e0e0e0",
                backgroundColor: "#f5f5f5",
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "& .media-overlay": { opacity: 1 },
                },
              }}
            >
              {isImage && <img src={media.fileUrl} alt="media" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
              {isVideo && (
                <Box sx={{ position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#000" }}>
                  <img src={getResolvedPosterUrl(media, BLACK_THUMBNAIL)} alt="thumbnail" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.7 }} onError={(e) => { e.target.onerror = null; e.target.src = BLACK_THUMBNAIL; }} />
                  <PlayCircleOutlineIcon sx={{ position: "absolute", color: "#fff", fontSize: "32px" }} />
                </Box>
              )}
              {isDoc && (
                <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 1, textAlign: "center", backgroundColor: "#EBF3FC" }}>
                  <InsertDriveFileIcon sx={{ color: "#0047AB", fontSize: "32px", mb: 0.5 }} />
                  <Typography noWrap sx={{ fontSize: "10px", width: "100%", fontWeight: 500, color: "#333" }}>{media.fileName || "Doc"}</Typography>
                </Box>
              )}

              {isUploading ? (
                <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                </Box>
              ) : (
                !isReadOnly && (
                  <Box
                    className="media-overlay"
                    sx={{
                      position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                      backgroundColor: "rgba(0,0,0,0.65)", backdropFilter: "blur(2px)",
                      opacity: 0, display: "flex", alignItems: "center", justifyContent: "center",
                      gap: 1, transition: "opacity 0.25s ease",
                    }}
                  >
                    <Tooltip title="Replace">
                      <IconButton size="small" onClick={() => triggerUpload(varIdx, slotIdx)} sx={{ color: "#fff", backgroundColor: "rgba(255,255,255,0.25)", "&:hover": { backgroundColor: "rgba(255,255,255,0.45)" } }}>
                        <EditIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteMedia(varIdx, slotIdx)} sx={{ color: "#fff", backgroundColor: "rgba(255,255,255,0.25)", "&:hover": { backgroundColor: "#DA4040" } }}>
                        <DeleteIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )
              )}
            </Box>
          );
        })}

        {/* Add media button */}
        {!isReadOnly && (
          <Tooltip title="Add media">
            <Box
              onClick={() => triggerUpload(varIdx, null)}
              sx={{
                width: 80, height: 80, minWidth: 80,
                borderRadius: "12px",
                border: "2px dashed #c0c0c0",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", backgroundColor: "#fafafa",
                "&:hover": { borderColor: "#0047AB", backgroundColor: "#EEF4FF" },
              }}
            >
              <AddPhotoAlternateIcon sx={{ color: "#aaa", fontSize: "28px" }} />
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: { xs: "12px", sm: "16px" },
          p: { xs: 1.5, sm: 2.5 },
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          mx: { xs: 1.5, sm: "auto" },
          width: { xs: "calc(100% - 24px)", sm: "100%" },
        },
      }}
    >
      <IconButton onClick={onClose} sx={{ position: "absolute", right: 16, top: 16, color: "#555" }}>
        <CloseIcon />
      </IconButton>

      <DialogTitle sx={{ fontWeight: "500", color: "#3B3B3B", fontSize: { xs: "16px", sm: "20px" }, p: 0, mb: 1, pr: 4 }}>
        {isReadOnly ? "View Scheduled Post" : "Edit Scheduled Post"}
      </DialogTitle>

      <DialogContent sx={{ px: 0, pt: 2 }}>
        {/* Content */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: "#3B3B3B", fontSize: "14px", fontWeight: 500, mb: 1 }}>Content</Typography>
          {isVariation ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {variations.map((v, i) => (
                <Box key={v.id || i} sx={{ border: "1px solid #e8e8e8", borderRadius: "10px", p: 1.5, backgroundColor: "#fafafa" }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={v.content || ""}
                    onChange={(e) => {
                      const newVars = [...variations];
                      newVars[i] = { ...newVars[i], content: e.target.value };
                      setVariations(newVars);
                    }}
                    disabled={isReadOnly}
                    variant="outlined"
                    label={`Variation ${i + 1}`}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        fontSize: "14px",
                      },
                    }}
                  />
                  {renderMediaRow(variationMediaList[i] || [], i)}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ border: "1px solid #e8e8e8", borderRadius: "10px", p: 1.5, backgroundColor: "#fafafa" }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={5}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                disabled={isReadOnly}
                variant="outlined"
                placeholder="Enter post content..."
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" } }}
              />
              {renderMediaRow(singleMediaList, null)}
            </Box>
          )}
        </Box>

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        />

        {/* Date & Time */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <Grid item xs={12} sm={6} sx={{ flex: 1, minWidth: { xs: "100%", sm: 0 }, position: "relative" }}>
            <Typography sx={{ color: "#3B3B3B", fontSize: "14px", fontWeight: 500, mb: 1 }}>Date</Typography>
            <TextField
              fullWidth
              value={date}
              variant="outlined"
              onClick={() => dateInputRef.current?.showPicker()}
              disabled={isReadOnly}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthOutlinedIcon sx={{ color: isReadOnly ? "#aaa" : "#1976d2", cursor: isReadOnly ? "default" : "pointer" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#f5f5f5" }, "& .MuiOutlinedInput-input": { padding: "10px 14px" } }}
            />
            <input
              type="date"
              ref={dateInputRef}
              value={date}
              min={getMinDate()}
              onChange={(e) => {
                const newDate = e.target.value;
                setDate(newDate);
                if (newDate === getMinDate() && time < getMinTime()) {
                  setTime(getMinTime());
                }
              }}
              disabled={isReadOnly}
              style={{ position: "absolute", top: 40, left: 0, opacity: 0, width: 0, height: 0, pointerEvents: "auto" }}
            />
          </Grid>

          <Grid item xs={12} sm={6} sx={{ flex: 1, minWidth: { xs: "100%", sm: 0 }, position: "relative" }}>
            <Typography sx={{ color: "#3B3B3B", fontSize: "14px", fontWeight: 500, mb: 1 }}>
              Time ({new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(new Date()).find(p => p.type === "timeZoneName")?.value || "Local"})
            </Typography>
            <TextField
              fullWidth
              value={time}
              variant="outlined"
              onClick={() => timeInputRef.current?.showPicker()}
              disabled={isReadOnly}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTimeIcon sx={{ color: isReadOnly ? "#aaa" : "#1976d2", cursor: isReadOnly ? "default" : "pointer" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#f5f5f5" }, "& .MuiOutlinedInput-input": { padding: "10px 14px" } }}
            />
            <input
              type="time"
              ref={timeInputRef}
              value={time}
              min={date === getMinDate() ? getMinTime() : undefined}
              onChange={(e) => {
                const newTime = e.target.value;
                if (date === getMinDate() && newTime < getMinTime()) {
                  setTime(getMinTime());
                } else {
                  setTime(newTime);
                }
              }}
              disabled={isReadOnly}
              style={{ position: "absolute", top: 40, left: 0, opacity: 0, width: 0, height: 0, pointerEvents: "auto" }}
            />
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 0, pt: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: { xs: "wrap", sm: "nowrap" }, gap: { xs: 1, sm: 2 } }}>
        {onDelete && (
          <Button
            onClick={() => { onDelete(scheduleData.id); onClose(); }}
            disabled={loading}
            sx={{ backgroundColor: "#FFF5F5", color: "#DA4040", borderRadius: "8px", textTransform: "none", border: "1px solid #FED6D6", px: { xs: 2, sm: 4 }, py: { xs: 0.75, sm: 1 }, fontWeight: 500, fontSize: { xs: "13px", sm: "16px" }, "&:hover": { backgroundColor: "#FEE2E2" } }}
          >
            Delete
          </Button>
        )}
        <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, ml: "auto" }}>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{ backgroundColor: "#F4F0FF", color: "#0047AB", borderRadius: "8px", textTransform: "none", border: "1px solid #F4F0FF", px: { xs: 2, sm: 4 }, py: { xs: 0.75, sm: 1 }, fontWeight: 500, fontSize: { xs: "13px", sm: "16px" }, "&:hover": { backgroundColor: "#ece9fd" } }}
          >
            Cancel
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSave}
              disabled={!isFormValid || loading}
              sx={{ backgroundColor: "#0047AB", color: "#fff", borderRadius: "8px", textTransform: "none", px: { xs: 2, sm: 4 }, py: { xs: 0.75, sm: 1 }, fontWeight: 500, fontSize: { xs: "13px", sm: "16px" }, "&:hover": { backgroundColor: "#002f8c" }, "&:disabled": { backgroundColor: "#ccc", color: "#fff" } }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditScheduleModal;
